/**
 * Exports JS des tokens (spec 05 §2) pour Framer Motion & GSAP.
 * Les couleurs vivent en variables CSS (colors.css) ; ici on expose :
 *  - des helpers pour les consommer (`rgbVar`, `readTokenRgb`) ;
 *  - les valeurs de mouvement/z-index utilisées en JS.
 * On n'écrit jamais un hex en dur dans un composant : tout passe par ces tokens.
 */

/** `var(--x)` prêt à l'emploi (style inline / GSAP). */
export const cssVar = (name: `--${string}`): string => `var(${name})`;

/** Couleur thème-aware : `rgb(var(--x) / alpha)` — suit `data-theme`. */
export const rgbVar = (name: `--${string}`, alpha = 1): string =>
  alpha >= 1 ? `rgb(var(${name}))` : `rgb(var(${name}) / ${alpha})`;

/**
 * Lit un token couleur « R G B » calculé (pour canvas/GSAP qui exigent une
 * couleur résolue). Retourne `rgb(R G B)`. Fallback si hors DOM (SSR/tests).
 */
export function readTokenRgb(name: `--${string}`, fallback = '255 90 31'): string {
  if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') {
    return `rgb(${fallback})`;
  }
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return `rgb(${raw || fallback})`;
}

/** Durées (ms) — mêmes valeurs que --dur-* (scale.css). */
export const durations = {
  instant: 80,
  fast: 150,
  base: 240,
  slow: 400,
  flip: 480,
} as const;

/** Easings — mêmes courbes que --ease-* (scale.css). */
export const easings = {
  /** cubic-bezier(.2,.8,.2,1) au format Framer. */
  fun: [0.2, 0.8, 0.2, 1] as [number, number, number, number],
  /** cubic-bezier(.34,1.56,.64,1) au format Framer. */
  bounce: [0.34, 1.56, 0.64, 1] as [number, number, number, number],
  funCss: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
  bounceCss: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
} as const;

/** Z-index — mêmes valeurs que --z-* (scale.css). */
export const zIndex = {
  base: 0,
  dropdown: 1000,
  sticky: 1100,
  overlay: 1200,
  modalBackdrop: 1300,
  modal: 1310,
  toast: 1400,
  tooltip: 1500,
} as const;

/** Noms des tokens couleur (pour `readTokenRgb`/`rgbVar` sans stringly-typing). */
export const colorTokens = {
  panda400: '--panda-400',
  panda500: '--panda-500',
  panda600: '--panda-600',
  panda700: '--panda-700',
  bamboo400: '--bamboo-400',
  bamboo500: '--bamboo-500',
  grape400: '--grape-400',
  grape500: '--grape-500',
  sky400: '--sky-400',
  sky500: '--sky-500',
  sun400: '--sun-400',
  sun500: '--sun-500',
  danger500: '--danger-500',
  ink950: '--ink-950',
  ink800: '--ink-800',
  ink700: '--ink-700',
  ink600: '--ink-600',
  ink400: '--ink-400',
  ink100: '--ink-100',
  ring: '--ring',
  motusCorrect: '--motus-correct',
  motusPresent: '--motus-present',
  motusAbsent: '--motus-absent',
} as const satisfies Record<string, `--${string}`>;
